import type { AppMessages } from "./ja";

export const enMessages: AppMessages = {
  localeLabel: "English",
  navigation: {
    sectionsAria: "Game sections",
    clickHint: "click!",
    tapHint: "tap!",
    draftPlaceholder: (label: string) => `${label} (Draft)`,
    items: {
      hovTarget: { label: "Hov", screenLabel: "Hov biome" },
      timTarget: { label: "Tim", screenLabel: "Tim biome" },
      coreloop3: { label: "Mat", screenLabel: "Mat biome" },
      coreloop4: { label: "Lev", screenLabel: "Lev biome" },
      mpm: { label: "MPM", screenLabel: "MPM" },
      achievements: { label: "Ach", screenLabel: "Achievements" },
      records: { label: "Rec", screenLabel: "Records" },
      help: { label: "Help", screenLabel: "Help" },
      settings: { label: "Settings", screenLabel: "Settings" },
      payment: { label: "Shop", screenLabel: "Shop" },
    },
  },
  standby: {
    label: "Standby mode",
    resumeAriaLabel: "Exit standby mode and return to the game",
  },
  common: {
    purchased: "Purchased",
    notPurchased: "Not purchased",
    receive: "Redeem",
    purchase: "Buy",
    loading: "Loading...",
    languageChangedReloading: "Language changed. Reloading...",
    hintLabel: "Hint",
    toastCloseAria: "Dismiss notification",
    formatMilliseconds: (milliseconds: number) => `${milliseconds}ms`,
  },
  mpm: {
    title: "MPM",
    subtitle: "Mouse Pointer Management",
    hovTargetsTitle: "Hov Targets",
    presets: {
      title: "Presets",
      unsaved: "Not saved",
      assignedMp: (amount: number) => `${amount} MP assigned`,
      save: "Save",
      load: "Load",
      saveAriaLabel: (presetNumber: number) =>
        `Save the current MPM state to preset ${presetNumber}`,
      loadAriaLabel: (presetNumber: number) =>
        `Load MPM preset ${presetNumber}`,
    },
    sections: {
      targets: "Targets",
      upgrades: "Upgrades",
      mhovExecution: "MHov",
      nhovExecution: "NHov",
      ohovExecution: "OHov",
      exploration: "Exploration",
      amplifierPanels: "Amp Panels",
      amplifierUpgrades: "Amp Upgrades",
    },
    actions: {
      clearAll: "Clear All",
      assignAll: "Assign All",
    },
    mhovExecutionInputs: {
      multiplier: "Minimum HG (x)",
      minimumResetIntervalSeconds: "Minimum time (s)",
    },
    nhovExecutionInputs: {
      minimumEffect: {
        hgAmplify: "Minimum HGU (x)",
        hgDiscount: "Minimum HGC (/)",
        hgNHovEffectsToHng: "Minimum HG NHov (x)",
        hngScale: "Minimum HnG RS (x)",
      },
      minimumResetIntervalSeconds: "Minimum time (s)",
    },
    ohovExecutionInputs: {
      minimumHovCore: "Minimum HC",
      minimumResetIntervalSeconds: "Minimum time (s)",
    },
    assignmentTooltips: {
      missingMp: (missingMp: number) => `${missingMp} MP short`,
    },
    clearAllAriaLabel: (section: string) =>
      `Clear all ${section} MP assignments`,
    assignAllAriaLabel: (section: string) =>
      `Assign MP to all ${section} slots`,
    sectionEnabledAriaLabel: (section: string) => `${section} section enabled`,
    sectionDisabledAriaLabel: (section: string) =>
      `${section} section disabled`,
    mpAvailabilityAriaLabel: (available: string, capacity: string) =>
      `MP available ${available} of ${capacity}`,
  },
  tutorial: {
    ok: "OK",
    progressLabel: "Progress",
    wellcome: "Wellcome to Hover Idle!",
    pcSteps: {
      intro:
        "Hover effects occur wherever you place your mouse pointer in this game.",
      target: "Hover over the target in the center of the screen to gain Hov.",
      upgrade:
        "Hover over the upgrade button at the bottom left of the screen to spend the cost and increase HG (Hov gain).",
      action: "First, try hovering over the OK button.",
    },
    mobileSteps: {
      intro:
        "Hover effects occur wherever you place the virtual pointer in this game.",
      action: "First, tap OK to place the virtual pointer.",
    },
    mobileNotice:
      "The virtual pointer remembers where it was placed. When you return, it restores the same hover target.",
  },
  settings: {
    title: "Settings",
    categoriesAria: "Settings categories",
    tabs: {
      general: "General",
      gameplay: "Gameplay",
      display: "Display",
      notifications: "Notifications",
      data: "Data",
    },
    general: {
      sectionTitle: "General",
      languageLabel: "Display language",
      languageDescription: "Reload the game after changing this setting.",
      languageOptions: {
        ja: "Japanese",
        en: "English",
      },
      hoverToggleDurationLabel: "Hover toggle duration",
      hoverToggleDurationDescription:
        "The time required for hover-based toggle actions such as MP assignment and TimHov activation.",
      hoverToggleDurationOptions: {
        short: "1 second",
        normal: "2 seconds",
      },
      hoverChargeDurationLabel: "Hover charge duration",
      hoverChargeDurationDescription:
        "The time required for sustained-hover actions such as Assign All, Clear All, and saving or loading presets.",
      hoverChargeDurationOptions: {
        short: "1 second",
        normal: "2 seconds",
      },
    },
    gameplay: {
      sectionTitle: "Gameplay",
      mHov: {
        sectionTitle: "MHov",
        chargeDurationLabel: "Tim to execute",
        chargeDurationOptions: {
          "0": "0 sec",
          "2000": "2 sec",
          "5000": "5 sec",
        },
        chargeDurationHint:
          "How long you need to hover the button before MHov is executed.",
        animationSpeedLabel: "Animation speed",
        animationSpeedOptions: {
          instant: "None",
          fast: "Fast",
          normal: "Normal",
        },
        animationSpeedReduceMotionTooltip:
          'Treated as "None" while reduced motion is enabled.',
      },
      nHov: {
        sectionTitle: "NHov",
        chargeDurationLabel: "Tim to execute",
        chargeDurationOptions: {
          "0": "0 sec",
          "2000": "2 sec",
          "5000": "5 sec",
        },
        chargeDurationHint:
          "How long you need to hover the button before NHov is executed.",
        animationSpeedLabel: "Animation speed",
        animationSpeedOptions: {
          instant: "None",
          fast: "Fast",
          normal: "Normal",
        },
        animationSpeedReduceMotionTooltip:
          'Treated as "None" while reduced motion is enabled.',
      },
      oHov: {
        sectionTitle: "OHov",
        chargeDurationLabel: "Tim to execute",
        chargeDurationOptions: {
          "0": "0 sec",
          "2000": "2 sec",
          "5000": "5 sec",
        },
        chargeDurationHint:
          "How long you need to hover the button before OHov is executed.",
        burstAnimationLabel: "Animation",
        burstAnimationOptions: {
          on: "On",
          off: "Off",
        },
        burstAnimationReduceMotionTooltip:
          'Treated as "Off" while reduced motion is enabled.',
      },
    },
    display: {
      sectionTitle: "Display",
      reduceMotion: "Reduced motion",
      reduceMotionHint:
        "Reduces non-essential animations. This does not affect safety delays, such as the time required before MHov executes.",
      lowPowerMode: "Low power mode",
      lowPowerModeHint:
        "Limits foreground drawing to about 30fps without changing game progression or input timing.",
      notationLabel: "Number notation",
      notationOptions: {
        standard: "Standard (B, T, Qa...)",
        scientific: "Scientific (1.23e10)",
        engineering: "Engineering (12.3e9)",
      },
    },
    notifications: {
      sectionTitle: "Notifications",
      achievementNotificationLabel: "Achievement notifications",
      wallNotificationLabel: "Wall notifications",
      helpNotificationsLabel: "Help notifications",
      helpNotificationsDescription:
        "Show a notification when new help topics become available.",
      achievementNotificationOptions: {
        off: "Off",
        dot: "Minimal",
        full: "Full",
      },
      wallNotificationOptions: {
        off: "Off",
        dot: "Minimal",
        full: "Full",
      },
    },
    data: {
      sectionTitle: "Data",
      backupHeading: "Save data",
      backupDescription:
        "Export your save data to a file, or import a file to restore it.",
      backupButton: "Backup (export)",
      loadButton: "Load (import)",
      backupSucceeded: "Backup file saved.",
      backupCancelled: "Backup was cancelled.",
      backupFailed: (reason: string) => `Backup failed: ${reason}`,
      loadFailed: (reason: string) => `Load failed: ${reason}`,
      rewardCodeHeading: "Reward codes",
      rewardCodeDescription:
        "Enter a code to receive rewards. Redeemed code status is kept even if you reset progress data.",
      rewardCodePlaceholder: "Enter code",
      rewardCodeAria: "Reward code",
      tokenSummary: (token: string) => `Token owned: ${token}`,
      rewardCodeEntry: (id: string, token: string) => `${id}: ${token} Token`,
      redeemed: "Redeemed",
      notRedeemed: "Not redeemed",
      rewardCodeEmpty: "Enter a reward code.",
      rewardCodeApplied: "Reward code applied.",
      rewardCodeAlreadyRedeemed: "This reward code has already been redeemed.",
      rewardCodeInvalid: "Invalid reward code.",
      dangerHeading: "Danger zone",
      dangerDescription:
        "Deletes normal progress and starts over. Reward codes and Shop purchase status are kept.",
      eraseButton: "Reset progress data",
      eraseButtonConfirm: "Click again to reset",
      eraseWarning: (seconds: number) =>
        `Warning: Press again within ${seconds} seconds to delete progress data.`,
      eraseCancelled: "Progress data reset was cancelled.",
      eraseFailed: (reason: string) =>
        `Failed to reset progress data: ${reason}`,
      eraseInProgress: "Resetting progress data...",
      account: {
        title: "Account",
        statusGuest: "You are currently playing as a guest.",
        statusGuestHint:
          "Sign in to make it easier to restore your saves and purchases later.",
        statusSignedIn: (identifier: string) => `Signed in as ${identifier}.`,
        statusSignedInHint:
          "Your saves and purchases are linked to this account.",
        emailLabel: "Email address",
        emailPlaceholder: "you@example.com",
        emailSubmit: "Send sign-in link",
        emailSent: "We sent a confirmation email. Please check your inbox.",
        oauthSubmit: (provider: string) => `Sign in with ${provider}`,
        oauthNotImplemented: "OAuth sign-in is not yet available.",
        errors: {
          invalidEmail: "Please enter a valid email address.",
          noAnonymousSession: "No guest session found.",
          linkFailed: "Sign-in failed. Please try again later.",
          generic: "An unexpected error occurred.",
        },
      },
    },
    draftPlaceholder: (label: string) => `${label} (coming soon)`,
  },
  offline: {
    summary: (elapsed: string, timGain: string | null) =>
      timGain === null
        ? `Offline rewards: away for ${elapsed}`
        : `Offline rewards: away for ${elapsed} / earned +${timGain} Tim`,
    rewardUnavailable: () =>
      "Offline rewards could not be granted. Please reload your save and try again.",
    rewardCommitRejected: () =>
      "Offline rewards could not be saved. Please reload your save and try again.",
    durationHoursMinutes: (hours: string, minutes: string) =>
      `${hours}h ${minutes}m`,
    durationMinutesSeconds: (minutes: string, seconds: string) =>
      `${minutes}m ${seconds}s`,
    durationSeconds: (seconds: string) => `${seconds}s`,
    modal: {
      title: "Offline Rewards",
      elapsedLabel: (elapsed: string) => `Away time: ${elapsed}`,
      appliedNotice:
        "* Due to the cap, rewards were granted for up to 24 hours.",
      timGainLabel: (amount: string) => `+${amount} Tim`,
      hoverAnchorNotice:
        "Hover Anchor has been restored to the previous hover target.",
      close: "Close",
      hoverClose: "Hover to close",
      closeAriaLabel: "Close offline rewards",
    },
  },
  achievements: {
    tabsAria: "Achievement categories",
    tabs: {
      normal: "Normal",
      secret: "Secret",
    },
    unlocked: "Unlocked",
    locked: "Locked",
    unlockedNotice: (names: string) => `Achievement unlocked: ${names}`,
    hovGainCountMultiplierLabel: (multiplier: number) =>
      `Achievement bonus: ×${multiplier.toFixed(2)} HG`,
    timGainCountMultiplierLabel: (multiplier: number) =>
      `Achievement bonus: ×${multiplier.toFixed(2)} TG`,
    names: {
      thousand: "Thousand",
      "first-merge-hov": "Merge Hov",
      "second-merge-hov": "Merge Hov Again",
      pop: "Pop!",
      bomb: "Bomb!",
      "hov-gain-family-upgrade": "Hover Upgrade",
      "tim-hov-activation": "Tim-Hov Activation",
      "time-has-come": "The Time Has Come",
      "global-hov-gain-upgrade-milestone": "HG Milestone",
      "ninth-hov-gain": "Ninth Hov Gain",
      "first-new-hov": "New Hov",
      "multi-branch-new-hov": "New New Hov",
      "many-new-hov": "Many New Hov",
      "skip-hov-gain": "Skip HG",
      "one-cost-batch-upgrade": "One-Cost, All In",
      "merge-hov-only-one-hov-target-cell": "Merge?",
      "new-hov-without-merge-hov": "New Hov Without Merge Hov",
      "outside-mhov": "Outside MHov",
      "outside-nhov": "Outside NHov",
      "amplifier-upgrade": "Amplifier Upgrade",
      "hov-1-gain-gets-serious": "H1G Gets Serious",
      "mass-hov-gain-family-upgrade": "Mass Hover Upgrade",
      "music-pointer": "Music Pointer",
      "movie-pointer": "Movie Pointer",
      "maschinen-pointer": "Maschinen Pointer",
      "hov-9-gain-upgrade-milestone": "H9G Milestone",
      vigintillion: "Vigintillion",
      "hov-1-gain-tries-too": "H1G Tries Too",
      google: "Google",
      "hov-gain-family-upgrade-marathon": "Hover Upgrade Marathon",
      "global-hov-gain-upgrade-tenth-milestone": "Rising HG",
      "not-fully-free": "Not 100% Off",
      "nhov-knows-nhov": "NHov Knows NHov",
      "first-outside-hov": "Outside Hov",
      "red-amplifier-full-charge": "Luminance",
      "massive-merge-hov": "Massive Merge Hov",
      "rank-gap": "Rank Gap",
      "second-outside-hov": "Outside Hov Again",
      "fast-outside-hov": "Fast Outside Hov",
      "thousand-ap": "Amplification",
      "tenth-outside-hov": "Farther Outside Hov",
      "mhov-mp-execution-ten": "Leave the Merge to MP",
      "quick-outside-hov": "Quick Outside Hov",
      "swift-outside-hov": "Swift Outside Hov",
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "HovHov",
      "fourth-hov-gain": "Fourth Hov Gain",
      "cheap-fourth-hov-gain": "Getting Cheaper.",
      "cheap-global-hov-gain-milestone": "Cheap Cheap Cheap",
      billion: "Billion",
      "burn-the-core": "Burn the Core",
      "mp-wall-preset": "Beyond the MP Wall",
      "exploration-tim-acceleration": "Borrowed Time",
      "exploration-biome-level-two": "Long View",
    },
    shortLabels: {
      thousand: "1k",
      "first-merge-hov": "MH",
      "second-merge-hov": "MH2",
      pop: "P",
      bomb: "B",
      "hov-gain-family-upgrade": "HU",
      "tim-hov-activation": "TH",
      "time-has-come": "Tim+",
      "global-hov-gain-upgrade-milestone": "HGM",
      "ninth-hov-gain": "H9G",
      "first-new-hov": "NH",
      "multi-branch-new-hov": "NNH",
      "many-new-hov": "NH10",
      "skip-hov-gain": "SHG",
      "one-cost-batch-upgrade": "1C",
      "merge-hov-only-one-hov-target-cell": "MH?",
      "new-hov-without-merge-hov": "NWM",
      "outside-mhov": "OM",
      "outside-nhov": "ON",
      "amplifier-upgrade": "AU",
      "hov-1-gain-gets-serious": "H1!",
      "mass-hov-gain-family-upgrade": "HU+",
      "music-pointer": "MP3",
      "movie-pointer": "MP4",
      "maschinen-pointer": "MP5",
      "hov-9-gain-upgrade-milestone": "H9GM",
      vigintillion: "VG",
      "hov-1-gain-tries-too": "H1+",
      google: "G",
      "hov-gain-family-upgrade-marathon": "HU*",
      "global-hov-gain-upgrade-tenth-milestone": "HG1k",
      "not-fully-free": "NH%",
      "nhov-knows-nhov": "NKN",
      "first-outside-hov": "OH",
      "red-amplifier-full-charge": "LU",
      "massive-merge-hov": "MMH",
      "rank-gap": "GAP",
      "second-outside-hov": "OH2",
      "fast-outside-hov": "FOH",
      "thousand-ap": "1kA",
      "tenth-outside-hov": "OH10",
      "mhov-mp-execution-ten": "MM10",
      "quick-outside-hov": "QOH",
      "swift-outside-hov": "SOH",
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "HH",
      "fourth-hov-gain": "H4G",
      "cheap-fourth-hov-gain": "CH4G",
      "cheap-global-hov-gain-milestone": "CCC",
      billion: "1b",
      "burn-the-core": "BTC",
      "mp-wall-preset": "MPW",
      "exploration-tim-acceleration": "BT",
      "exploration-biome-level-two": "LV",
    },
    conditions: {
      thousand: "Have 1,000 Hov",
      "first-merge-hov": "Perform MHov for the first time.",
      "second-merge-hov": "Perform MHov for the second time.",
      pop: (formattedThreshold: string) =>
        `Perform an MHov worth at least ${formattedThreshold} HG.`,
      bomb: (formattedThreshold: string) =>
        `Perform an MHov worth at least ${formattedThreshold} HG.`,
      "hov-gain-family-upgrade": (count: number) =>
        `Perform HG and HnG upgrades ${count} times.`,
      "tim-hov-activation": (quantity: number) =>
        `Refine ${quantity} Hov from Tim while hovering Target`,
      "time-has-come": (quantity: number) =>
        `Spend a total of ${quantity} Tim.`,
      "global-hov-gain-upgrade-milestone": (level: number) =>
        `Reach HG upgrade level ${level}.`,
      "ninth-hov-gain": "Unlock the ninth Hov target.",
      "first-new-hov": "Perform NHov for the first time.",
      "multi-branch-new-hov": "Perform 2 or more types of NHov.",
      "many-new-hov": "Perform NHov 10 times in total.",
      "skip-hov-gain":
        "Unlock the ninth Hov target while no targets other than the first target are unlocked.",
      "one-cost-batch-upgrade": (level: number) =>
        `Execute HG upgrade level ${level} at 1 cost.`,
      "merge-hov-only-one-hov-target-cell":
        "Perform MHov with only one Hov Cell.",
      "new-hov-without-merge-hov": "Perform NHov without performing MHov.",
      "outside-mhov": "Perform an MHov worth 1.8e308 HG.",
      "outside-nhov": "Perform an NHov with a /1.8e308 HG cost.",
      "amplifier-upgrade": (count: number) =>
        `Perform Amplifier upgrades ${count} times.`,
      "hov-1-gain-gets-serious": (formattedThreshold: string) =>
        `Make H1G at least ${formattedThreshold}.`,
      "mass-hov-gain-family-upgrade": (count: number) =>
        `Perform HG and HnG upgrades ${count} times in total.`,
      "music-pointer": (count: number) => `Assign ${count} MP.`,
      "movie-pointer": (count: number) => `Assign ${count} MP.`,
      "maschinen-pointer": (count: number) => `Assign ${count} MP.`,
      "hov-9-gain-upgrade-milestone": (level: number) =>
        `Reach H9G upgrade level ${level}.`,
      vigintillion: (formattedThreshold: string) =>
        `Own ${formattedThreshold} Hov.`,
      "hov-1-gain-tries-too": (formattedThreshold: string) =>
        `Make H1G at least ${formattedThreshold}.`,
      google: (formattedThreshold: string) => `Own ${formattedThreshold} Hov.`,
      "hov-gain-family-upgrade-marathon": (count: number) =>
        `Perform HG and HnG upgrades ${count} times.`,
      "global-hov-gain-upgrade-tenth-milestone": (level: number) =>
        `Reach HG upgrade level ${level}.`,
      "not-fully-free": (formattedThreshold: string) =>
        `Perform an NHov whose hgDiscount HG cost divisor exceeds /${formattedThreshold}.`,
      "nhov-knows-nhov": (formattedThreshold: string) =>
        `Strengthen an HG NHov effect by x${formattedThreshold} or more and pass it to HnG."`,
      "first-outside-hov": "Trigger OHov for the first time.",
      "red-amplifier-full-charge": "Fully charge the Red Amplifier.",
      "massive-merge-hov": (formattedThreshold: string) =>
        `Perform an MHov worth at least ${formattedThreshold} in a single execution.`,
      "rank-gap": (formattedThreshold: string) =>
        `Make H9G at least ${formattedThreshold} of H1G.`,
      "second-outside-hov": "Trigger OHov for the second time.",
      "fast-outside-hov": (hours: number) =>
        `Trigger OHov within ${hours} hour.`,
      "thousand-ap": "Have 1,000 AP.",
      "tenth-outside-hov": "Trigger the 10th OHov.",
      "mhov-mp-execution-ten": "Let MP execute MHov 10 times.",
      "quick-outside-hov": (minutes: number) =>
        `Trigger OHov within ${minutes} minutes.`,
      "swift-outside-hov": (minutes: number) =>
        `Trigger OHov within ${minutes} minute.`,
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "Allocate the HovTim balance slider to Hov",
      "fourth-hov-gain": "Unlock the fourth Hov target.",
      "cheap-fourth-hov-gain": "Unlock H4G for 1 cost.",
      "cheap-global-hov-gain-milestone": "Trigger an HG milestone for 1 cost.",
      billion: (formattedThreshold: string) => `Own ${formattedThreshold} Hov.`,
      "burn-the-core": (count: number) => `Spend ${count} HovCore in total.`,
      "mp-wall-preset": "Clear the MP Wall.",
      "exploration-tim-acceleration": (seconds: number) =>
        `Advance exploration time by a total of ${seconds} second through the Tim biome effect in Exploration.`,
      "exploration-biome-level-two": (level: number) =>
        `Reach level ${level} in every Exploration biome.`,
    },
    effectLabel: "Effect: ",
    effects: {
      thousand: "",
      "first-merge-hov": "",
      "second-merge-hov": (multiplier: number) => `×${multiplier} HG`,
      pop: "",
      bomb: "",
      "hov-gain-family-upgrade":
        "HG and HnG hover-repeat upgrades start faster.",
      "tim-hov-activation": "Unlock control of TimHov activation",
      "time-has-come": (multiplier: number) =>
        `x${multiplier} TimHov activation efficiency`,
      "global-hov-gain-upgrade-milestone": (level: number) =>
        `Learn that HG milestones trigger every ${level} HG upgrade levels.`,
      "ninth-hov-gain": (amount: number) => `+${amount} MP`,
      "first-new-hov": "",
      "multi-branch-new-hov": (multiplier: number) => `HG x${multiplier}`,
      "many-new-hov": "",
      "skip-hov-gain": "",
      "one-cost-batch-upgrade":
        "One-cost hover repeat upgrades are purchased in batches.",
      "merge-hov-only-one-hov-target-cell": "",
      "new-hov-without-merge-hov": "",
      "outside-mhov": "MHov can be performed at 1.8e308 HG or beyond.",
      "outside-nhov": "All NHov can be performed at 1.8e308 or beyond.",
      "amplifier-upgrade":
        "Amplifier upgrade hover-repeat starts much sooner, and MP can be assigned to Amplifier upgrades.",
      "hov-1-gain-gets-serious": (bonus: number) => `+${bonus} H1G exponent`,
      "mass-hov-gain-family-upgrade": (amount: number) =>
        `+${amount} MP. Enables MP assignment to HG and HnG upgrades.`,
      "music-pointer": (amount: number) => `+${amount} MP`,
      "movie-pointer": (amount: number) => `+${amount} MP`,
      "maschinen-pointer": (amount: number) => `+${amount} MP`,
      "hov-9-gain-upgrade-milestone": "",
      vigintillion: "",
      "hov-1-gain-tries-too": (bonus: number) => `+${bonus} H1G exponent`,
      google: (bonus: number) => `+${bonus} HG exponent`,
      "hov-gain-family-upgrade-marathon":
        "HG and HnG upgrade hover repeat starts even sooner.",
      "global-hov-gain-upgrade-tenth-milestone": (bonus: number) =>
        `+${bonus} HG exponent`,
      "not-fully-free": (divisor: number) => `/${divisor} HG and HnG cost`,
      "nhov-knows-nhov": "",
      "first-outside-hov": "",
      "red-amplifier-full-charge": "x1.1 RAG",
      "massive-merge-hov": (bonus: number) => `+${bonus} HG exponent`,
      "rank-gap": (bonus: number) => `+${bonus} HG exponent`,
      "second-outside-hov": "",
      "fast-outside-hov": "",
      "thousand-ap": "",
      "tenth-outside-hov": (bonus: number) => `+${bonus} HG exponent`,
      "mhov-mp-execution-ten": (bonus: number) => `+${bonus} MP`,
      "quick-outside-hov": "",
      "swift-outside-hov": "",
      // EA scope: HovTim balance slider achievement is deferred.
      // "hov-hov": "",
      "fourth-hov-gain": (amount: number) => `+${amount} MP`,
      "cheap-fourth-hov-gain": (divisor: number) =>
        `/${divisor} HG and HnG cost`,
      "cheap-global-hov-gain-milestone": (divisor: number) =>
        `/${divisor} HG and HnG cost`,
      billion: "",
      "burn-the-core": (skillCoordinate: string) =>
        `Skill ${skillCoordinate} scales with OHov count.`,
      "mp-wall-preset": "MP Preset +1",
      "exploration-tim-acceleration": "Unlock Exploration acceleration.",
      "exploration-biome-level-two": (amount: number) => `+${amount} MP`,
    },
    secrets: {
      maskedName: "???",
      maskedShortLabel: "?",
      names: {
        observer: "Observer",
        "heavy-pointer": "Heavy Pointer",
        "x-pattern-hover": "X",
        "hov-o-label": "hOver",
        "h-pattern-unlock": "H is for Hov",
        "h-pattern-hover": "H is for Hover",
        "target-rapid-click": "This Is Not a Cookie",
      },
      shortLabels: {
        observer: "OBS",
        "heavy-pointer": "HM",
        "x-pattern-hover": "X",
        "hov-o-label": "hO",
        "h-pattern-unlock": "H",
        "h-pattern-hover": "H",
        "target-rapid-click": "TC",
      },
      hints: {
        observer: "There is no need to rush.",
        "heavy-pointer": "Keep the pointer on the Target.",
        "x-pattern-hover": "Make an X shape blink.",
        "hov-o-label": "Something in the HUD looks hoverable.",
        "h-pattern-unlock": "An H shape might be possible.",
        "h-pattern-hover": "Make an H shape blink.",
        "target-rapid-click": "The Target might be a cookie.",
      },
      effects: {
        observer: "",
        "heavy-pointer": "",
        "x-pattern-hover": (token: number) => `${token} Token`,
        "hov-o-label": (token: number) => `${token} Token`,
        "h-pattern-unlock": (token: number) => `${token} Token`,
        "h-pattern-hover": (token: number) => `${token} Token`,
        "target-rapid-click": (token: number) => `${token} Token`,
      },
      conditions: {
        observer:
          "Watch the Hov target screen for 30 seconds without earning Hov",
        "heavy-pointer": (minutes: number) =>
          `Keep hovering a Target for ${minutes} minutes straight`,
        "x-pattern-hover": "Hold the Hov Target in an X-shaped hover state",
        "hov-o-label": (seconds: number) =>
          `Hover over the “o” in Hov on the HUD for ${seconds} seconds.`,
        "h-pattern-unlock": "Unlock Hov targets in an H shape",
        "h-pattern-hover": "Hold the Hov Target in an H-shaped hover state",
        "target-rapid-click": (count: number, windowSeconds: number) =>
          `Click the Target ${count} times within ${windowSeconds} seconds.`,
      },
    },
  },
  records: {
    tabsAria: "Records categories",
    tabs: {
      stats: "Stats",
      history: "History",
      purchases: "Purchases",
    },
    statsDraftNotice: "Stats are not available yet.",
    historyEmpty: "No history yet.",
    purchasesEmpty: "No purchases yet.",
    productNames: {
      starter_bundle: "Starter Bundle",
      tim_boost_bundle: "Tim Boost Bundle",
      token_pack_100jpy: "Token 1000",
      token_pack_500jpy: "Token 5500",
      token_pack_2000jpy: "Token 30000",
      token_pack_5000jpy: "Token 65000",
    },
    unknownProduct: (id: string) => id,
    purchaseLine: (date: string, productLabel: string) =>
      `${date}  Purchased ${productLabel}`,
    tokenLine: (date: string, delta: string, productLabel: string) =>
      `${date}  Token ${delta} (${productLabel})`,
    tokenDeltaPositive: (amount: string) => `+${amount}`,
    tokenDeltaNegative: (amount: string) => `-${amount}`,
    stats: {
      sectionPlaytime: "Playtime",
      onlinePlaytime: "Online playtime",
      totalPlaytime: "Total time",
      sectionHov: "Hov",
      currentHov: "Current Hov",
      maxHov: "Max Hov reached",
      sectionTim: "Tim",
      totalTimEarned: "Total Tim earned",
      sectionSpecial: "Special",
      mHovCount: "MHov count",
      nHovCount: "NHov count",
      oHovPower: "OHov Count",
      oHovFastest: "Fastest OHov",
      oHovCurrentTime: "Current OHov time",
      totalHovCoreSpent: "Total HovCore Spent",
      sectionAchievements: "Achievements",
      normalAchievements: "Normal",
      secretAchievements: "Secret",
      sectionInfo: "Info",
      lastSavedAt: "Last saved",
      firstPlayedAt: "First played",
      notAvailable: "—",
      formatPlaytime: (totalSeconds: number): string => {
        const s = Math.max(0, Math.floor(totalSeconds));
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (d > 0) return `${d}d ${h}h ${m}m`;
        if (h > 0) return `${h}h ${m}m ${sec}s`;
        if (m > 0) return `${m}m ${sec}s`;
        return `${sec}s`;
      },
      formatDateTime: (timestamp: number): string => {
        const d = new Date(timestamp);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
      },
    },
  },
  help: {
    tabsAria: "Help",
    unreadLabel: "New help is available",
    tabs: {
      basics: "Basics",
      hov: "Hov",
      tim: "Tim",
    },
    basics: {
      pcDescription:
        "Hover Idle is an idle game where you progress by placing your mouse pointer over targets on the screen.",
      mobileDescription:
        "Hover Idle is an idle game where you progress by placing a virtual pointer over targets on the screen.",
      items: [
        "Hover over the target in the center of the Hov Target screen to collect Hov.",
        "Spend the Hov you collect on upgrades to increase how much Hov you gain.",
        "New things may happen as you collect Hov and unlock achievements.",
        "Collect lots of Hov!",
      ],
      virtualPointer: {
        heading: "Virtual Pointer",
        description:
          "In Hover Idle, a virtual pointer is placed where you tap.",
        items: [
          "Hover effects occur where the virtual pointer is placed. Menus and confirmation screens use normal tap controls.",
          "The virtual pointer remembers where it was last placed. When you return to the screen, it restores the same hover target.",
        ],
      },
      offline: {
        heading: "Offline Rewards",
        description: "Earn Tim based on how long you are offline.",
        items: [
          "Offline rewards can cover up to 24 hours.",
          "While away, normal Hov, reset, and Wall gameplay does not advance; only Tim is calculated from reward time.",
        ],
      },
      system: {
        heading: "System Screens",
        pcDescription:
          "System screens such as Settings and Shop are controlled by clicking.",
        mobileDescription:
          "System screens such as Settings and Shop are controlled by tapping.",
      },
    },
    hov: {
      description: "Hov is the basic resource earned through hover input.",
      items: [
        "Keep the pointer over the Target or cells to gain Hov.",
        "Upgrades in the bottom-left of each target increase your Hov gain.",
        "More Hov features unlock gradually as progression expands.",
      ],
      mHov: {
        heading: "MHov",
        description:
          "Merge Target (MHov) is a merge action that resets part of your Hov-side progress to strengthen your Hov gain.",
        items: [
          "When executed, your Hov gain is strengthened by the effect value shown on the right side of the button.",
          "The left side of the button shows your current multiplier.",
        ],
        resetHeading: "Items reset",
        resetItems: ["Owned Hov", "HG Upgrade", "HnG Upgrade"],
        keepHeading: "Main items kept",
        keepItems: [
          "Tim",
          "Fai",
          "MHov effect",
          "Cumulative stats",
          "Settings",
        ],
        warning:
          "Note: After executing MHov, short-term Hov-side progress starts over. Check the after value and reset targets before executing.",
      },
      nHov: {
        heading: "NHov / New Hov",
        description:
          "NHov is a reset that clears part of the Hov section in exchange for persistent Hov-side bonuses.",
        items: [
          "Tim-side progress is kept.",
          "The first NHov uses HG Upgrade amplification. After the first Stock Wall clear, other branches can be selected on the first NHov.",
        ],
        resetHeading: "Main reset targets",
        resetItems: [
          "Current Hov",
          "Hov Upgrades",
          "HnG Upgrades",
          "MHov effects",
          "Temporary Hov-section progress",
        ],
        keepHeading: "Main kept progress",
        keepItems: [
          "Tim",
          "Tim Upgrades",
          "Tim Cell progress",
          "Tim Skills",
          "NHov bonuses",
          "Cumulative stats",
          "Settings",
        ],
        warning:
          "Note: After executing NHov, short-term Hov-side progress starts over. Check the reset targets before executing.",
      },
    },
    tim: {
      description: "Tim is a resource gained through elapsed time.",
      timHovActivation: {
        beforePc: "Hover Target with the mouse cursor to convert Tim into Hov.",
        beforeMobile:
          "Hover Target with the virtual pointer to convert Tim into Hov.",
        afterPc:
          "While hovering Target, use the HUD arrow to choose whether Tim is converted into Hov.",
        afterMobile:
          "While hovering Target with the virtual pointer, use the HUD arrow to choose whether Tim is converted into Hov.",
      },
      items: [],
    },
  },
  hovSkill: {
    regionAria: "Hov skill",
    title: "Hov Skills",
    influenceLabel: "Influence",
    costLabel: "Cost",
    externalLabel: "External",
    costResources: {
      hovCore: "HovCore",
    },
    none: "None",
    stateAcquired: "Acquired",
    stateMissingPrerequisites: "Missing prerequisites",
    stateMissingResource: (missing: string, resource: string) =>
      `Need ${missing} more ${resource}`,
    stateHoldToAcquire: (seconds: string) =>
      `Hover for ${seconds} seconds to acquire`,
    holdDurationLabel: "Hold duration",
    holdDurationOption: (seconds: string) => `${seconds}s`,
    names: {
      "amplifier-unlock": "Amplifier",
      "hg-exponent-bonus": "Hov Exponent Boost",
      "hg-hng-simultaneous-purchase-count-base-bonus":
        "HG and HnG Simultaneous Purchase",
      "mp-capacity-bonus": "MP Boost",
      "hov-1-gain-multiplier-bonus": "H1G Gain Boost",
      "amplifier-gain-ohov-multiplier-bonus": "OHov AG Boost",
      "ohov-hov-core-gain-multiplier-bonus": "HovCore Gain Multiplier",
      "mhov-mp-control-unlock": "MHov Control",
      "nhov-mp-control-unlock": "NHov Control",
      "variety-pack": "Variety Pack",
      "amplifier-panel-mp-placement-unlock": "Amp Panel MP Placement",
      "mp-capacity-second-bonus": "MP Expansion II",
      "mp-capacity-core": "MP Capacity Core",
      "free-mp-core": "Free MP Core",
      "wall-cap-relaxation": "Wall Cap Relaxation",
      "exploration-unlock": "Exploration",
      "hng-rank-scale-base-bonus": "HnG Rank Scale Base+",
      "wall-unlock": "Wall",
      "ra-plum-amplifier-gain-multiplier-bonus": "RA Plum",
      "ra-bamboo-amplifier-gain-multiplier-bonus": "RA Bamboo",
      "ra-pine-amplifier-gain-multiplier-bonus": "RA Pine",
      "ap-boar-exponent-bonus": "AP Boar",
      "ap-deer-exponent-bonus": "AP Deer",
      "ap-butterfly-exponent-bonus": "AP Butterfly",
    },
    shortLabels: {
      "amplifier-unlock": "Amp",
      "hg-exponent-bonus": "HE+",
      "hg-hng-simultaneous-purchase-count-base-bonus": "HUC+",
      "mp-capacity-bonus": "MP+",
      "hov-1-gain-multiplier-bonus": "H1G×",
      "amplifier-gain-ohov-multiplier-bonus": "AG×",
      "ohov-hov-core-gain-multiplier-bonus": "HC×",
      "mhov-mp-control-unlock": "MHov←",
      "nhov-mp-control-unlock": "NHov←",
      "variety-pack": "VP",
      "amplifier-panel-mp-placement-unlock": "AP-MP",
      "mp-capacity-second-bonus": "MP++",
      "mp-capacity-core": "MP-CC",
      "free-mp-core": "F-MP-C",
      "wall-cap-relaxation": "WCR",
      "exploration-unlock": "Explore",
      "hng-rank-scale-base-bonus": "HnG+",
      "wall-unlock": "Wall",
      "ra-plum-amplifier-gain-multiplier-bonus": "RA-A",
      "ra-bamboo-amplifier-gain-multiplier-bonus": "RA-B",
      "ra-pine-amplifier-gain-multiplier-bonus": "RA-C",
      "ap-boar-exponent-bonus": "AP-α",
      "ap-deer-exponent-bonus": "AP-β",
      "ap-butterfly-exponent-bonus": "AP-γ",
    },
    descriptions: {
      "amplifier-unlock": "Unlocks the Amplifier tab.",
      "hg-exponent-bonus": "Strengthens the HG exponent.",
      "hg-hng-simultaneous-purchase-count-base-bonus":
        "Increases the simultaneous purchase count for HG and HnG.",
      "mp-capacity-bonus": "Increases the MP capacity.",
      "hov-1-gain-multiplier-bonus": "Strengthens the Hov cell 1 gain (H1G).",
      "amplifier-gain-ohov-multiplier-bonus":
        "Strengthens the RAG based on OHov.",
      "ohov-hov-core-gain-multiplier-bonus": {
        base: "Increases the HovCore gain on OHov.",
        scaling: "Increases the HovCore gain on OHov based on the OHov count.",
      },
      "mhov-mp-control-unlock":
        "Enables MP assignment for MHov execution conditions in MPM.",
      "nhov-mp-control-unlock":
        "Allows MP assignment in MPM for NHov execution conditions.",
      "variety-pack":
        "Strengthens the growth exponents of MHov, NHov1, NHov3, and NHov4.",
      "amplifier-panel-mp-placement-unlock":
        "Enables MP assignment to Amplifier panels.",
      "mp-capacity-second-bonus": "Expands the MP capacity.",
      "mp-capacity-core":
        "Increases HovCore gained from OHov based on MP capacity.",
      "free-mp-core":
        "Increases HovCore gained from OHov based on unassigned MP.",
      "wall-cap-relaxation": "Relaxes reward influence caps through Wall 6.",
      "exploration-unlock": "Unlocks the Exploration tab.",
      "hng-rank-scale-base-bonus": "Strengthens the HnG rank scale base value.",
      "wall-unlock": "Unlocks the Wall tab.",
      "ra-plum-amplifier-gain-multiplier-bonus": "Strengthens RA gain.",
      "ra-bamboo-amplifier-gain-multiplier-bonus":
        "Strengthens RA gain by cleared wall kinds.",
      "ra-pine-amplifier-gain-multiplier-bonus":
        "RA gain increases with current OHov run time.",
      "ap-boar-exponent-bonus": "Strengthens the AP exponent.",
      "ap-deer-exponent-bonus":
        "Strengthens the AP exponent based on unlocked amplifier kinds.",
      "ap-butterfly-exponent-bonus": "Strengthens the AP exponent based on AP.",
    },
  },
  wall: {
    regionAria: "Wall",
    title: "Wall",
    clearedNotice: "Step Wall cleared",
    rewardLabel: "Reward",
    firstClearRewardLabel: (amount: string) =>
      `First clear bonus: +${amount} HovCore`,
    firstClearRewardClaimedLabel: (amount: string) =>
      `First clear bonus (claimed): +${amount} HovCore`,
    firstClearRewardTextLabel: (reward: string) =>
      `First clear bonus: ${reward}`,
    firstClearRewardClaimedTextLabel: (reward: string) =>
      `First clear bonus (claimed): ${reward}`,
    firstClearRewards: {
      ohovMpControlUnlock:
        "Enables MP assignment for OHov execution conditions in MPM.",
      nhovFirstBranchSelectionUnlock:
        "Allows a branch other than NHov1 to be selected for the first NHov.",
      amplifierLaDaAvailabilityUnlock:
        "Unlocks LA / DA visibility, purchases, and MP assignment.",
    },
    currentMultiplierLabel: "Influence",
    currentMultiplierCapReachedLabel: "Influence (cap reached)",
    rewardEffectMultiplyFormat: (value: string) => `x${value}`,
    rewardEffectAddFormat: (value: string) => `+${value}`,
    rewardEffectBuiltInOHovAutoTargetFormat: (value: string) => `${value} Hov`,
    rewardEffectRaPropagationInfluenceFormat: (value: string) =>
      `RA bonus propagation x${value}`,
    rewardEffectUnacquired: "Not acquired",
    bestClearLabel: "Best clear",
    notClearedYet: "Not cleared yet",
    inProgressLabel: "In progress",
    startLabel: "Start",
    startAriaLabel: (wallName: string) => `Start ${wallName}`,
    stopAriaLabel: (wallName: string) => `Stop ${wallName}`,
    active: "Active",
    inactive: "Inactive",
    startStopChargeDurationLabel: "Wall start/stop time",
    startStopChargeDurationOption: (seconds: string) => `${seconds}s`,
    walls: {
      "step-wall": {
        name: "Step Wall",
        constraint: "Even-numbered Hov Cells cannot be unlocked.",
        reward: "HG based on clear time.",
        clearedNotice: "Step Wall cleared",
      },
      "price-wall": {
        name: "Price Wall",
        constraint: "NHov2 (/n HG cost) cannot be used.",
        reward:
          "Increases simultaneous HG and HnG purchase count based on clear time.",
        clearedNotice: "Price Wall cleared",
      },
      "mp-wall": {
        name: "MP Wall",
        constraint:
          "MP assignment to Hov targets and Hov Gain Upgrades is limited to one shared dedicated slot.",
        reward: "Increases MP based on clear time.",
        clearedNotice: "MP Wall cleared",
      },
      "freshness-wall": {
        name: "Freshness Wall",
        constraint: "MHov and NHov boosts weaken over time.",
        reward: "Strengthens the MHov growth exponent based on clear time.",
        clearedNotice: "Freshness Wall cleared",
      },
      "outer-wall": {
        name: "Outer Wall",
        constraint: "Wall clear requires 4.2e720 Hov.",
        reward: "OHov auto execution Hov limit based on clear time.",
        clearedNotice: "Outer Wall cleared",
      },
      "split-wall": {
        name: "Split Wall",
        constraint:
          "Targets can split only once. Clearing requires 3.9e660 Hov.",
        reward: "Increases H1G based on clear time",
        clearedNotice: "Split Wall cleared",
      },
      "hg-wall": {
        name: "HG Wall",
        constraint: "HG / 1e10, HG^0.5",
        reward: "Increases HG exponent based on clear time",
        clearedNotice: "HG Wall cleared",
      },
      "stock-wall": {
        name: "Stock Wall",
        constraint: "NHov can be executed only 5 times",
        reward: "Increases the Stock Wall NHov limit based on clear time",
        clearedNotice: "Stock Wall cleared",
      },
      "amplification-wall": {
        name: "Amplification Wall",
        constraint:
          "Unavailable Amplifiers activate in a corrupted state and weaken lower-tier Amplifier Gain.",
        reward:
          "Propagates RA-only Amplifier Gain bonuses to OA through DA based on clear time.",
        clearedNotice: "Amplification Wall cleared",
      },
    },
  },
  exploration: {
    regionAria: "Exploration",
    title: "Exploration",
    mpUsageAria: "MP usage",
    mpUsage: (used: string, total: string) => `${used} / ${total} MP`,
    areaAria: (biome: string) => `${biome} exploration area`,
    biomes: {
      hov: { name: "Hov biome", compact: "Hb" },
      tim: { name: "Tim biome", compact: "Tb" },
      mat: { name: "Mat biome", compact: "Mb" },
      lev: { name: "Lev biome", compact: "Lb" },
    },
    biomeHeading: (biome: string, level: string) => `${biome} (${level})`,
    effectLabel: (effect: string) => `Effect: ${effect}`,
    assignMpSlotAria: (amount: string) => `Assign the ${amount} MP slot`,
    removeMpSlotAria: (amount: string) =>
      `Remove the ${amount} MP slot assignment`,
    timAcceleration: {
      title: (tim: string) => `Exploration acceleration ${tim} Tim`,
      activeEffect: (cost: string, multiplier: string) =>
        `When active: -${cost} Tim/s · Exploration ×${multiplier}`,
      toggleAria: (enabled: boolean, insufficientTim: boolean): string =>
        insufficientTim
          ? "Exploration acceleration enabled; not enough Tim"
          : enabled
            ? "Disable Exploration acceleration"
            : "Enable Exploration acceleration",
    },
    mpSlotPurchase: {
      name: "MP Slot",
      tooltipKind: "Exploration Upgrade",
      tooltipName: "MP Slot",
      price: (amount: string) => `${amount} HC`,
      completed: "Complete",
      description:
        "Allows additional MP to be assigned to the target exploration biome.",
      effect: (biome: string, amount: string) => `+ ${biome} ${amount} MP slot`,
      allUnlockedEffect: "All MP slots unlocked",
      noCost: "Price not configured",
      noRemainingCost: "—",
      states: { ready: "Available" },
    },
    cost: (amount: string) => `Cost ${amount} HovCore`,
    seconds: (seconds: string) => `${seconds}s`,
    effects: {
      awardOHov: (amount: string) => `+${amount} OHov`,
      addExplorationTime: (duration: string) => `Exploration time +${duration}`,
      multiplyHg: (multiplier: string) => `×${multiplier} HG`,
      raiseBiomeLevel: (amount: string) => `Exploration Biome Level +${amount}`,
    },
    blockedReasons: {
      "exploration-locked": "Exploration is locked",
      "price-unconfigured": "Price not configured",
      "insufficient-hov-core": "Not enough HovCore",
      "all-mp-slots-unlocked": "All slots unlocked",
      "invalid-mp-slot": "This MP slot is unavailable",
      "mp-slot-locked": "This MP slot is locked",
      "mpm-section-disabled": "The Exploration MPM section is disabled",
      "already-assigned": "This MP slot is already assigned",
      "global-mp-capacity-exceeded": "Not enough available MP",
      "not-assigned": "This MP slot is not assigned",
    },
  },
  timSkill: {
    regionAria: "Tim skill",
    title: "Tim Skills",
    effectLabel: "Effect",
    costLabel: "Cost",
    none: "None",
    stateAcquired: "Acquired",
    stateMissingPrerequisites: "Missing prerequisites",
    stateMissingResource: (missing: string) => `Need ${missing} more Tim`,
    stateHoldToAcquire: (seconds: string) =>
      `Hover for ${seconds} seconds to acquire`,
    names: {
      GridIncreaseTim: "GridIncreaseTim",
      GridIncreaseTim2: "GridIncreaseTim2",
    },
    shortLabels: {
      GridIncreaseTim: "GIT",
      GridIncreaseTim2: "GIT^2",
    },
    effects: {
      GridIncreaseTim: "TimTarget splits into 4 cells",
      GridIncreaseTim2: "TimTarget splits into 9 cells",
    },
  },
  resourceHud: {
    ariaLabel: "HoverIdle resources",
    hovWordH: "H",
    hovWordO: "o",
    hovWordV: "v",
    hovValueSuffix: " ",
    hovWordAriaLabel: "Hov",
    hovOAriaLabel: "Secret hover spot",
    timSeparator: " | ",
    timSuffix: " Tim",
    fai: (value: string) => ` | Fai ${value}`,
    recentRate: (value: string) => `Recent rate: ${value} Hov/s`,
    hovGain: (value: string) => `${value} HG`,
    timGain: (value: string) => `${value} TG`,
    timHovActivationAriaLabel: "Tim-Hov activation",
    hovCoreHudAriaLabel: "HoverIdle HC and Hov resources",
    hovCoreAmount: (value: string) => `${value} HC`,
    hovCoreRecentRate: (value: string) => `Recent rate: ${value} HC/h`,
    hovAmount: (value: string) => `${value} Hov`,
    oHovPowerAmount: (value: string) => `${value} OHov`,
    hovCoreAmountAriaLabel: "hovCore amount",
    hovAmountAriaLabel: "Hov amount",
    oHovPowerAmountAriaLabel: "OHovPower amount",
  },
  hovView: {
    tabsAria: "Hov biome tabs",
    region: "Hov biome",
    mainTab: "Target",
    skillTab: "Skills",
    skillTabLocked: (threshold: string) => `${threshold} < Hov`,
    skillTabUnlockNotice: "Hov Skills tab unlocked",
    amplifierTab: "Amp",
    amplifierTabUnlockNotice: "Amplifier tab unlocked",
    wallTab: "Wall",
    wallTabUnlockNotice: "Wall tab unlocked",
    explorationTab: "Exploration",
    amplifierPanelDescription: "Hov Skills unlock amplification elements here.",
    amplifier: {
      hgFormulaSqrt: "HG × √AP",
      hgFormulaExponentBase: "HG × AP",
      upgradeLabel: "Upgrade",
      levelLabel: (level: string) => `Lv. ${level}`,
      outputLevel: (level: string) => `Output Lv. ${level}`,
      amplifyLabel: "Amplify",
      lockedLabel: "Locked",
      corruptedLabel: "Corrupted",
      entryTitle: (entryLabel: string) => `${entryLabel} Amplifier`,
      upgradeButtonAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplifier output upgrade`,
      amplifyPanelAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplify panel`,
      corruptedPanelAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplifier, corrupted and unavailable`,
      panelMpSlotAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplifier panel MP assignment`,
      upgradeMpSlotAriaLabel: (entryLabel: string) =>
        `${entryLabel} Amplifier upgrade MP assignment`,
      gaugeLabel: "Amplification Light gauge",
      remainingLabel: "Remaining",
      upgradeCostNone: "Cost: —",
      upgradeCost: (value: string) => `Cost: ${value} HC`,
      tooltipSummary:
        "Amplification Light burns, generating power for each amplifier.",
      guard: {
        label: "Upgrade Guard",
        description:
          "Mis-hover prevention time for Amplifier upgrades that spend HC.",
        options: {
          0: "None",
          1000: "1s",
          3000: "3s",
        },
      },
      entries: {
        red: "Red",
        orange: "Orange",
        yellow: "Yellow",
        green: "Green",
        cyan: "Cyan",
        blue: "Blue",
        purple: "Purple",
        light: "Light",
        dark: "Dark",
      },
      gainUpgradeTooltip: {
        effectLabel: "Effect",
        effect: (delta: string, shortLabel: string) =>
          `+${delta} ${shortLabel}`,
        afterLabel: (shortLabel: string) => `${shortLabel} after upgrade`,
        after: (current: string, next: string) => `${current} -> ${next}`,
        costLabel: "Cost",
        cost: (value: string) => `${value} HC`,
        stateLabel: "State",
        stateReady: "Ready",
        stateNeed: (missing: string) => `Need ${missing} HC`,
        byEntry: {
          red: {
            title: "Red Amplifier Gain",
            summary: "Increases AP gained from the red amplifier.",
          },
          orange: {
            title: "Orange Amplifier Gain",
            summary: "Increases RAG gained from the orange amplifier.",
          },
          yellow: {
            title: "Yellow Amplifier Gain",
            summary: "Increases OAG gained from the yellow amplifier.",
          },
          green: {
            title: "Green Amplifier Gain",
            summary: "Increases YAG gained from the green amplifier.",
          },
          cyan: {
            title: "Cyan Amplifier Gain",
            summary: "Increases GAG gained from the cyan amplifier.",
          },
          blue: {
            title: "Blue Amplifier Gain",
            summary: "Increases CAG gained from the blue amplifier.",
          },
          purple: {
            title: "Purple Amplifier Gain",
            summary: "Increases BAG gained from the purple amplifier.",
          },
          light: {
            title: "Light Amplifier Gain",
            summary: "Increases AP gained from the light amplifier.",
          },
          dark: {
            title: "Dark Amplifier Gain",
            summary: "Increases AP gained from the dark amplifier.",
          },
        },
      },
    },
    upgradeCardAria: "Hov gain upgrade details",
    hngDrawer: {
      label: "HnG",
      expandAriaLabel: "Show HnG upgrades",
      collapseAriaLabel: "Hide HnG upgrades",
    },
    upgradeTooltip: {
      effectLabel: "Effect",
      costLabel: "Cost",
      stateLabel: "State",
      effectEnhancementLevels: (count: string, rawCount: number) =>
        `+${count} enhancement ${rawCount === 1 ? "level" : "levels"}`,
      global: {
        title: "Hov Gain Amount",
        summary: "Increases the amount of Hov gained.",
        effectDelta: (delta: string) => `+${delta} HG`,
        effectMultiplier: (multiplier: string) => `${multiplier} HG`,
        afterLabel: "HG after",
      },
      cell: {
        title: (index: number) => `Hov ${index} Gain Amount`,
        summary: (index: number) =>
          `Increases the amount of Hov gained from cell ${index}.`,
        effectDelta: (index: number, delta: string) => `+${delta} H${index}G`,
        effectMultiplier: (index: number, multiplier: string) =>
          `${multiplier} H${index}G`,
        afterLabel: (index: number) => `H${index}G after`,
      },
      cost: (value: string) => `${value} Hov`,
      batchCost: (unitCost: string, count: string) =>
        `${unitCost} x ${count} Hov`,
      costLabelBatch: (count: string) => `Cost (${count})`,
      wallBlockedCost: "— Hov",
      after: (current: string, next: string) => `${current} -> ${next}`,
      stateReady: "Ready",
      stateNeed: (missing: string) => `Need ${missing} Hov`,
      stateWallBlocked: {
        "step-wall": "Disabled by Step Wall",
        "price-wall": "Disabled by Price Wall",
        "mp-wall": "Disabled by MP Wall",
        "freshness-wall": "Disabled by Freshness Wall",
        "outer-wall": "Disabled by Outer Wall",
        "split-wall": "Disabled by Split Wall",
        "hg-wall": "Disabled by HG Wall",
        "stock-wall": "Disabled by Stock Wall",
        "amplification-wall": "Disabled by Amplification Wall",
      },
    },
    mHov: {
      btnLabel: (before: string, after: string) =>
        `Merge Target: x${before} HG → x${after} HG`,
      btnLabelMobile: (before: string, after: string) =>
        `MHov: x${before} → x${after} HG`,
      btnLabelFreshnessWall: (
        current: string,
        fresh: string,
        effective: string,
      ) => `x${current} HG → x${fresh} HG → x${effective} HG`,
      ariaLabel: "Merge Target",
    },
    nHov: {
      header: "NHov",
      goToNHovPanelAriaLabel: "Go to NHov panel",
      returnToCellsPanelAriaLabel: "Return to Hov Cells",
      branchHeading: {
        hgAmplify: "xN HG Upgrade",
        hgDiscount: "/n HG Cost",
        hgNHovEffectsToHng: "HG NHov xN HnG",
        hngScale: "HnG Rank Scale",
      },
    },
  },
  timView: {
    tabsAria: "Tim biome tabs",
    region: "Tim biome",
    mainTab: "Target",
    skillTab: "Skills",
    balanceTab: "Balance",
    balanceTabLocked: (threshold: string) => `${threshold} < Tim`,
    balanceTabUnlockNotice: "Balance tab unlocked",
    skillTabLocked: (threshold: string) => `${threshold} < Hov`,
    upgradeCardAria: "Tim gain upgrade details",
    upgradeTooltip: {
      title: "Tim Gain Amount",
      summary: "Permanently increases Tim generation per second.",
      afterLabel: "TG after",
    },
    upgradeEffect: (value: string) => `+${value} TG`,
    upgradeCost: (value: string) => `Cost: ${value} Tim`,
    upgradeAfter: (current: string, next: string) => `${current} -> ${next}`,
    upgradeStateReady: "Ready",
    upgradeStateNeed: (missing: string) => `Need ${missing} Tim`,
  },
  upgradeTooltip: {
    kicker: "Upgrade",
    effect: "Effect",
    cost: "Cost",
    state: "State",
  },
  cellUpgrade: {
    upgradeEffect: (gain: string) => `+${gain}`,
    upgradeAfter: (current: string, next: string) => `${current} -> ${next}`,
    actionPrefix: "Cost: ",
    upgradeCost: (value: string, unit: string) => `${value} ${unit}`,
    upgradeStateReady: "Ready",
    upgradeStateNeed: (missing: string, unit: string) =>
      `Need ${missing} ${unit}`,
  },
  hoverTarget: {
    hovCellAria: (index: number) => `Hov target ${index}`,
    timCellAria: (index: number) => `Tim target ${index}`,
    mpSlotAria: (index: number) => `Mouse pointer slot ${index}`,
    wallMpSlotAria: (target: string) => `MP Wall slot: ${target}`,
    lockBadge: "🔒",
  },
  balance: {
    regionAria: "HovTim balance",
    title: "HovTim Balance",
    total: (value: string) => `HovTim ${value}`,
    gain: (value: string) => `HovTimG ${value}`,
    unassigned: (value: string) => `Unassigned ${value}`,
    resourceSliderAria: "Hov Tim allocation ratio",
    gainSliderAria: "HG TG allocation ratio",
    assignAria: "Assign on hover",
    unassignAria: "Unassign on hover",
    assign: "Assign",
    unassign: "Unassign",
    assignHint: "Hover to add",
    unassignHint: "Hover to remove",
    gainUnlockNotice: "HG/TG allocation unlocked",
  },
  payment: {
    title: "Shop",
    categoriesAria: "Shop categories",
    tokenSummary: (token: string) => `Token owned: ${token}`,
    tabs: {
      effects: "Effects",
      tokens: "Tokens",
      bundles: "Bundles",
      credits: "Credits",
    },
    effectsDescription:
      "Spend Token on permanent effects. Purchases on this screen remain even after a full reset.",
    acquiredHeading: "Acquired Effects",
    acquiredEmpty: "No acquired effects yet.",
    acquiredEffectEntry: (name: string, effect: string) =>
      `${name} — ${effect}`,
    tokensDescription:
      "Purchase Token. Token can be bought any time and used for Shop effects and other permanent upgrades.",
    bundlesDescription:
      "Bundles are bought once as entitlements, and their local rewards are applied only once.",
    tokensPlaceholder: "Tokens (Draft)",
    headers: {
      effectCost: "Cost",
      effectName: "Name",
      effectValue: "Permanent effect",
      bundleName: "name(sku)",
      bundleContents: "contents",
      bundlePrice: "price",
      tokenAmount: "Token",
      tokenPrice: "price",
    },
    effectCostValue: (cost: string) => `${cost} Token`,
    effectMaxedCost: "—",
    effectOwnedCount: (count: string, max: string) => `${count}/${max}`,
    effectTotalMpCapacity: (amount: string) => `+${amount} MP`,
    effectTotalHovGainPercent: (percent: string) => `+${percent}% HG`,
    effectTotalTimGainPercent: (percent: string) => `+${percent}% TG`,
    tokenPackAmount: (token: string) => `${token} Token`,
    statusPurchasedEffect: "Permanent effect purchased.",
    statusPurchasedBundle: "Bundle purchased and rewards applied.",
    statusPurchasedTokenPack: "Token purchased.",
    statusAlreadyPurchasedEffect: "This permanent effect is already purchased.",
    statusAlreadyPurchasedBundle: "This bundle is already owned.",
    statusInsufficientToken: "Not enough Token.",
    statusBundleCheckoutFailed: "Bundle purchase failed.",
    statusTokenPackCheckoutFailed: "Token purchase failed.",
    statusCheckoutUnavailable:
      "Checkout is unavailable in this build. Token and Bundle purchases cannot be completed.",
    permanentNames: {
      addMousePointer: "Add Mouse Pointer",
      hovGainPercent: "HG Boost",
      timGainPercent: "TG Boost",
    },
    permanentEffects: {
      addMousePointer: "+1 MP",
      hovGainPercent: "+10% HG",
      timGainPercent: "+5% TG",
    },
    bundleNames: {
      starter_bundle: "Starter Bundle",
      tim_boost_bundle: "Tim Boost Bundle",
    },
    bundlePartSeparator: " / ",
    bundlePartToken: (token: string) => `${token} Token`,
    bundlePartHov: (hov: string) => `+${hov} Hov`,
    bundlePartTim: (tim: string) => `+${tim} Tim`,
    signInRecommendation: {
      title: "Sign-in Recommended",
      body: [
        "We recommend signing in so your purchases can be safely restored.",
        "You can continue as a guest, but recovery may be difficult if you clear your browser data or switch devices.",
      ],
      actions: {
        signIn: "Sign in",
        continueAsGuest: "Continue as guest",
        cancel: "Cancel",
      },
    },
    purchaseLegalNotice: {
      title: "Before Purchase",
      agreement:
        "By continuing with the purchase, you are deemed to agree to the Terms of Service and Privacy Policy.",
      details:
        "You can review refund conditions and seller information from the pages below.",
      stripeFootnote:
        "When Stripe Checkout is introduced, the same policy links and support contact will also be displayed on Checkout.",
      links: {
        terms: "Terms of Service",
        privacy: "Privacy Policy",
        commerce: "Commercial Disclosure",
      },
    },
    credits: {
      title: "Credits",
      badge: "Draft",
      description:
        "HoverIdle-specific legal and operator drafts. These pages are organized for use from the Shop purchase flow and include game-specific rules, Stripe payments, and save-data behavior.",
      sectionsAria: "Credits categories",
      sections: {
        operations: {
          label: "Operator Info",
          title: "Operator Info",
          lead: "This section summarizes the HoverIdle operator, contact route, and purchase support details. The formal legal seller listing is provided in the commercial disclosure section.",
          items: [
            "Operator: AkiWeb Hakoniwa",
            "Service name: HoverIdle",
            "Site URL: https://akiweb8528.net/",
            "Support contact: akiweb8528@gmail.com",
            "Supported languages: Japanese / English",
            "Purchase flow: Paid products are planned to use an external payment page such as Stripe Checkout.",
          ],
          note: "Note: The formal seller fields such as representative, phone number, and address are presented in the commercial disclosure section.",
        },
        terms: {
          label: "Terms of Service",
          title: "Terms of Service",
          lead: "This is a HoverIdle-specific Terms of Service draft. It is organized around idle-game save behavior, balance adjustments, payments, and prohibited conduct.",
          items: [
            "The service is a browser-based idle game, and its game content, release scope, and service features may change at the operator’s discretion.",
            "Save data is primarily stored in local browser storage such as localStorage. Cloud save is not guaranteed at this time.",
            "The operator does not guarantee restoration of save data lost because of browser setting changes, device failure, cache deletion, or storage removal.",
            "Offline rewards, progression speed, reward multipliers, prices, material costs, unlock conditions, and other balance values may be adjusted before or after a purchase.",
            "Paid items are granted according to what is displayed at the time of purchase. If subscription products are introduced later, their billing cycle, price, and cancellation method will be displayed separately.",
            "Because the products are digital, refunds or cancellations are generally not available except where required by law or where the purchase process fails. If subscriptions are introduced, cancellation will generally stop the next renewal.",
            "Bots, macros, automation tools, scripts, tampering, cheating, exploit abuse, reverse engineering, payment abuse, and harassment are prohibited.",
            "If a violation or suspected fraud is found, the operator may limit or suspend progress, payment status, or access to the service.",
            "For maintenance, incidents, legal compliance, or operational reasons, the operator may suspend or change part or all of the service with or without prior notice.",
            "Except in cases of intentional misconduct or gross negligence, the operator is not liable for damages arising from use of, or inability to use, the service.",
            "These terms are governed by Japanese law, and disputes relating to the service are subject to the exclusive jurisdiction of the Kobe District Court at first instance.",
          ],
          note: "Note: Before release, this draft should be converted into a formal clause-based agreement with definitions, notice methods, and amendment procedures.",
        },
        privacy: {
          label: "Privacy Policy",
          title: "Privacy Policy",
          lead: "This is a HoverIdle-specific privacy draft written for Stripe payments, game progression data, and browser-side storage.",
          items: [
            "Collected data may include email address, payment-related identifiers, inquiry content, cookies, localStorage, analytics data, game progression data, device/browser data, and IP address.",
            "Processing purposes include service delivery, purchase verification, item grants, identity confirmation, fraud prevention, support handling, quality improvement, and incident investigation.",
            "Game progression and setting data are primarily stored in browser-side storage on the user’s device.",
            "If cloud save is introduced later, the storage location, retention period, and restoration policy should be documented in this policy or a dedicated screen.",
            "Stripe is used for payments, and payment processing is also subject to Stripe systems and policies.",
            "If hosting, logging, or support vendors are used, necessary data may be shared with them to the extent required for operations.",
            "If an analytics tool is introduced, the final policy should identify the provider, the data collected, and how users can disable it.",
            "When Stripe or other external providers are used, information may be processed outside Japan where necessary.",
            "Data is not disclosed to third parties without consent except where required by law, needed for anti-abuse measures, or necessary to protect rights.",
            "Requests for access, correction, deletion, or restriction should be handled after identity verification.",
          ],
          note: "Note: Before release, replace placeholders with the actual hosting provider, analytics tools, retention periods, and support route used in production.",
        },
        commerce: {
          label: "Commercial Disclosure",
          title: "Commercial Disclosure",
          lead: "This draft is based on the existing AkiWeb Hakoniwa legal disclosure and extended to cover HoverIdle digital sales terms.",
          items: [
            "Seller: AkiWeb Hakoniwa",
            "Service name: HoverIdle",
            "Representative: Disclosed without delay upon request",
            "Address: 6-6 Yokomichi-cho, Nishinomiya, Hyogo 662-0917, Japan",
            "Phone number: Disclosed without delay upon request",
            "Email address: akiweb8528@gmail.com",
            "Sales price: Tax-included pricing is shown on each product page or Shop screen.",
            "Additional costs: Internet access fees and communication charges are borne by the customer.",
            "Payment method: Credit card and other methods displayed on the purchase screen through Stripe.",
            "Payment timing: At the completion of the purchase flow. If subscriptions are introduced, at each renewal.",
            "Delivery timing: Usually reflected in-game immediately after successful payment, but delays can occur because of network or system issues.",
            "Returns and refunds: Because the products are digital, returns and refunds are generally not available except where required by law or in cases such as non-delivery or duplicate billing.",
            "Mid-term cancellation: If subscriptions are introduced, they can be cancelled before the next renewal by the method separately displayed at that time.",
            "Recommended environment: Latest major versions of Chrome, Firefox, and Safari.",
          ],
          note: "Note: Stripe Checkout is planned to display refund policy details, support contact information, and links to the Terms of Service and Privacy Policy as well.",
        },
      },
    },
  },
  system: {
    loadFailureMessage:
      "Save data could not be loaded, so a new game has been started.",
  },
};
